import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import DropdownList from 'react-widgets/DropdownList';
import UncontrolledTextInput from 'app/components/common/UncontrolledTextInput.jsx';
import ConstantMixin from 'app/mixins/ConstantMixin.js';
import { Breakpoint, useCurrentBreakpointName } from 'react-socks';
import HelpButton from 'app/components/common/HelpButton.jsx';

const urlRegex = /(\b(((https?|ftp|file|):\/\/)|www[.])[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/ig;

const AITextbox = (props) => {
    let buttonClass = "run-modifier",
        promptClass = 'ai-textbox',
        buttonAction = null,
        rewind = null,
        clear = null;

    if (typeof props.currentPrompt === "string" && props.currentPrompt.trim() !== "") {
        buttonClass += " active";
        buttonAction = props.applyModifier;
    }

    if (props.hasHistory > 1) {
        rewind = (
            <div className="ai-rewind" role="button" aria-label="Go to last version" onClick={props.rewind}>
                <i className="icon ion-ios-skipbackward"/>
            </div>
        );
    }

    if (props.hasHistory > 0) {
        clear = (
            <div className="ai-clear-button" role="button" aria-label="Go to original draft" onClick={props.resetStack}>
                <i className="icon ion-close-round"/>
            </div>
        );
    }

    return (
        <div className={promptClass}>
            {props.isProcessing == false && props.mode === 'guided' && props.hasHistory === 0 ? (
                <div className={buttonClass} tabIndex="0" ref={props.triggerRef} role="button" aria-label="Generate AI content" onClick={(e) => {
                    if (buttonAction) {
                        buttonAction('');
                    }
                }}>
                    <i className="icon ion-android-send"/>
                </div>
            ) : null}
            {props.isProcessing ? (
                <div className="modifier-running">
                    <i className="fa fa-spinner fa-spin"></i>
                </div>
            ) : null}
            {props.isProcessing ? (
                <UncontrolledTextInput
                    placeholder={props.placeholder}
                    className={props.isTextArea ? 'ai-textarea' : 'ai-textfield'}
                    onChange={props.updatePrompt}
                    value={props.currentPrompt}
                    isTextArea={props.isTextArea}
                    rows={props.isTextArea ? 4 : 1}
                    disabled="disabled"/>
            ) : (
                <UncontrolledTextInput
                    placeholder={props.placeholder}
                    className={props.isTextArea ? 'ai-textarea' : 'ai-textfield'}
                    onChange={props.updatePrompt}
                    value={props.currentPrompt}
                    isTextArea={props.isTextArea}
                    rows={props.isTextArea ? 4 : 1}/>
            )}
            {!props.isProcessing ? (
                clear
            ) : null}
            {!props.isProcessing ? (
                rewind
            ) : null}
        </div>
    );
};

const OriginalText = (props) => {
    let helperText = <span>Your original text <HelpButton which="aiDisclosure"/></span>;

    if (props.mode === 'guided' && props.currentGuide === 'link') {
        helperText = <span>The URL used to generate a post <HelpButton which="aiDisclosure"/></span>;
    }

    return (
        <div className="ai-original-wrapper">
            <div className="ai-original-copy">
                {props.responseStack[0]}
            </div>
            <div className="helper-text">
                {helperText}
            </div>
        </div>
    );
}

const AIModifiers = (props) => {
    const [ showModifierMenu, setShowModifierMenu ] = useState(false);
    const imageUrl = props.app.config.assetsUrl + props.app.config.imageUrl;

    let aiMods = [
            {
                value: 'rephrase',
                label: 'Rephrase',
                icon: 'icon ion-android-refresh',
                image: null
            },
            {
                value: 'shorter',
                label: 'Shorter',
                icon: '',
                image: `${imageUrl}/misc/shorter.svg`
            },
            {
                value: 'longer',
                label: 'Longer',
                icon: '',
                image: `${imageUrl}/misc/longer.svg`
            },
            {
                value: 'simpler',
                label: 'Simpler',
                icon: 'icon ion-android-checkmark-circle',
                image: null
            },
            {
                value: 'casual',
                label: 'More Casual',
                icon: 'icon ion-tshirt',
                image: null
            },
            {
                value: 'professional',
                label: 'More Professional',
                icon: 'icon ion-briefcase',
                image: null
            }
        ],
        modifierMenu = null;

    if (showModifierMenu) {
        modifierMenu = (
            <ul className="ai-modifier-menu">
                {aiMods.map((mod) => {
                    let icon = null;
                    if (mod.icon !== '') {
                        icon = <i className={mod.icon}/>
                    } else {
                        icon = <img src={mod.image}/>
                    }

                    return (
                        <li key={`option-${mod.value}`} role="button" aria-label={mod.label} onClick={() => {
                            props.onClick(mod.value);
                            setShowModifierMenu(false);
                        }}>
                            <div className="ai-modifier-icon">
                                {icon}
                            </div>
                            <div className="ai-modifier-label">
                                {mod.label}
                            </div>
                        </li>
                    );

                })}
            </ul>
        );
    }

    return (
        <div className="ai-modifiers-wrapper">
            <div className="ai-modifier-trigger" role="button" aria-label="Show edit options" onClick={() => {
                if (showModifierMenu) {
                    setShowModifierMenu(false);
                } else {
                    setShowModifierMenu(true);
                }
            }}>
                <i className="icon ion-android-options"/> Edit with AI <i className="icon ion-chevron-down"/>
            </div>
            {modifierMenu}
        </div>
    );
}

const AIHelper = (props) => {
    const [ responseStack, setResponseStack ] = useState([]);
    const [ isProcessing, setIsProcessing ] = useState(false);
    const [ mode, setMode ] = useState('guided');
    const [ currentPrompt, setCurrentPrompt ] = useState('');
    const [ currentGuide, setCurrentGuide ] = useState('prompt');
    const [ error, setError ] = useState('');
    const [ guid, setGuid ] = useState('');
    const [ uuid, setUUID] = useState('');
    const [ enterPressed, setEnterPressed ] = useState(false);
    const aiTriggerRef = useRef(null);
    const insertRef = useRef(null);
    const currentBreakpoint = useCurrentBreakpointName();

    const applyModifier = (modifier) => {
        let nextResponseStack = [...responseStack],
            requestData = {
                link: '',
                text: '',
                keywords: '',
                transform: '',
                guid: guid
            };

        if (mode === 'guided' && currentGuide === 'link' && responseStack.length == 0) {
            if (currentPrompt.match(urlRegex) === null) {
                setError("Please provide a valid link.");
                return;
            }
        }

        nextResponseStack.push(currentPrompt);
        setResponseStack(nextResponseStack);

        if (error !== '') {
            setError('');
        }

        if (mode === 'guided' && currentGuide === 'link' && responseStack.length === 0) {
            requestData.link = currentPrompt;
        } else if (mode === 'guided' && currentGuide === 'keywords' && responseStack.length === 0) {
            requestData.keywords = currentPrompt;
        } else {
            requestData.text = currentPrompt;
        }

        requestData.transform = (modifier) ? modifier : '';

        setIsProcessing(true);

        axios({
            url: `${props.app.config.apiUrl}/post/aiPostHelper`,
            data: requestData,
            headers: { "Content-type": "application/json" },
            method: 'POST',
            withCredentials: true,
            responseType: 'json'
        }).then((response) => {
            setIsProcessing(false);
            let nextPrompt = response.data.response;

            if (requestData.link) {
                nextPrompt += ` ${requestData.link}`;
            }

            setCurrentPrompt(nextPrompt);
            setGuid(response.data.guid);

        }).catch((error) => {
            let reason = (error.response && error.response.data && error.response.data.errors) ? error.response.data.errors.aiHelper : '';

            setIsProcessing(false);
            // aiPostHelper_api says why when it couldn't write anything; anything else gets the general word
            setError(reason || "Sorry, we weren't able to process your last request.");

        });

        if (props.recordEvent) {
            props.recordEvent({
                pub_id: props.user.detail.id,
                event_name: 'Modify Text with AI Helper',
                event_data: {
                    request: {
                        link: (requestData.link) ? true : false,
                        keywords: (requestData.keywords) ? true : false,
                        text: (requestData.text) ? true : false,
                        modifier: requestData.modifier,
                        uuid: uuid
                    }
                }
            });
        }

    };

    const updatePrompt = (e) => {
        setCurrentPrompt(e.target.value);
    }

    const rewind = (e) => {
        let nextResponseStack = [...responseStack],
            nextPrompt = '';

        nextPrompt = nextResponseStack.pop();

        setResponseStack(nextResponseStack);
        setCurrentPrompt(nextPrompt);
    }

    const resetStack = (e) => {

        setCurrentPrompt('');
        setResponseStack([]);
        setGuid('');
        if (self.crypto && typeof self.crypto.randomUUID === "function") {
            setUUID(self.crypto.randomUUID());
        }

    }

    let guidePrompts = [
        {
            type: 'prompt',
            label: (currentBreakpoint === 'desktop') ? 'Generate post from text' : 'From text'
        },
        {
            type: 'link',
            label: (currentBreakpoint === 'desktop') ? 'Generate post from link' : 'From link'
        },
        {
            type: 'keywords',
            label: (currentBreakpoint === 'desktop') ? 'Generate post from Keywords' : 'From keywords'
        }
    ];

    const enterListener = (e) => {
        if (e.keyCode === 13) {
            setEnterPressed(true);
        }
    }

    // on load effect
    useEffect(() => {
        let serviceId = (props.accessoryServiceId !== undefined && props.accessoryServiceId) ? parseInt(props.accessoryServiceId) : 0,
            copy = '';

        if (props.mode === 'prompted') {
            if (serviceId) {
                if (props.postData.services[serviceId].message) {
                    copy = props.postData.services[serviceId].message;
                }
            } else {
                if (props.postData.shared.message) {
                    copy = props.postData.shared.message;
                }
            }
        }

        if (props.mode === 'guidedlink') {
            setMode("guided");
            setCurrentGuide("link");
        } else {
            setMode(props.mode);
        }

        setCurrentPrompt(copy);


        if (self.crypto && typeof self.crypto.randomUUID === "function") {
            setUUID(self.crypto.randomUUID());
        }

        if (props.recordEvent) {
            props.recordEvent({
                pub_id: props.user.detail.id,
                event_name: 'Load AI Helper',
                event_data: {
                    mode: props.mode,
                    uuid
                }
            });
        }

        window.addEventListener('keyup', enterListener);

        return () => {
            window.removeEventListener('keyup', enterListener);
        }

    }, []);

    useEffect(() => {
        if (enterPressed && aiTriggerRef.current && aiTriggerRef.current.className.match("active")) {
            applyModifier('');
            setEnterPressed(false);
        }
    }, [enterPressed])

    let prompts = null,
        startOver = <div/>,
        buttonLabel = "Insert into post",
        isTextArea = true,
        helperText = '',
        placeholder = '',
        guideButtons = null;

    if (mode === 'guided') {
        let guideClass = "guide-buttons";
        if (currentBreakpoint === 'mobile') {
            guideClass += ' mobile';
        }
        guideButtons = (
            <div className={guideClass}>
                {guidePrompts.map((prompt) => {
                    let buttonClass = 'guide-prompt';
                    if (prompt.type === currentGuide) {
                        buttonClass += ' active';
                    }

                    return (
                        <div key={`prompt-${prompt.type}`} className={buttonClass} role="button" aria-label={prompt.label} onClick={() => {
                            setCurrentGuide(prompt.type);
                            setCurrentPrompt('');
                            setError('');
                        }}>
                            {prompt.label}
                        </div>
                    );

                })}
            </div>
        );

        if (currentGuide === 'link') {
            isTextArea = false;
        }

        switch (currentGuide) {
            case 'prompt':
                helperText = <span>Write a prompt to help the AI Helper generate a post for you. <HelpButton which="aiDisclosure"/></span>;
                placeholder = 'What do you want to write about?';
                break;

            case 'link':
                helperText = <span>Enter a URL to help the AI Helper generate a post for you.<HelpButton which="aiDisclosure"/></span>;
                placeholder = 'Enter link to generate post';
                break;

            case 'keywords':
                helperText = <span>Enter keywords to help the AI Helper generate a post for you.<HelpButton which="aiDisclosure"/></span>;
                placeholder = 'Add a comma-separated list of keywords here';
                break;

            default:
                break;
        }

        if (responseStack.length >= 1) {
            helperText = null;
            isTextArea = true;
        }

    }

    prompts = (
        <div>
            {responseStack.length === 0 && mode === 'guided' ? (
                guideButtons
            ) : null }
            {responseStack.length > 0 ? (
                <div className="original-prompt">
                    <OriginalText
                        mode={mode}
                        currentGuide={currentGuide}
                        responseStack={responseStack}/>
                </div>
            ) : null }
            <div className="ai-prompt-wrapper">
                {error ? (
                    <div className="ai-prompt-error">{error}</div>
                ) : null}
                <AITextbox
                    isProcessing={isProcessing}
                    currentPrompt={currentPrompt}
                    updatePrompt={updatePrompt}
                    isTextArea={isTextArea}
                    placeholder={placeholder}
                    applyModifier={applyModifier}
                    mode={mode}
                    hasHistory={responseStack.length}
                    rewind={rewind}
                    resetStack={resetStack}
                    triggerRef={aiTriggerRef}/>
                <div className="helper-text">{helperText}</div>
            </div>
            {(responseStack.length > 0 || mode === 'prompted') ? (
               <AIModifiers
                    {...props}
                    onClick={(modifier) => {
                        applyModifier(modifier);
                    }}/>
            ) : null}
        </div>
    );

    let mainAction = null,
        mainActionClass = 'ai-helper-insert';

    if (responseStack.length > 0) {
        mainActionClass += ' active';
        mainAction = () => {
            props.updateMessage({
                message: currentPrompt,
                uuid: uuid
            });
        };

        startOver = (
            <div className="start-over-button" role="button" aria-label="Reset to original text" onClick={resetStack}>
                start over
            </div>
        );
    }

    if (props.accessoryServiceId !== 0) {
        let serviceName = ConstantMixin.getPostServiceNameByCode(props.accessoryServiceId);
        buttonLabel = `Insert into ${serviceName} post`;
    }

    return (
        <div className="post-tool-ai-helper">
            <div className="ai-helper-header">
                AI Helper ✨
            </div>
            <div className="ai-helper-prompts">
                {prompts}
            </div>
            <Breakpoint desktop only>
                <div className="ai-actions">
                    {startOver}
                    <div className={mainActionClass} tabIndex="0" role="button" ref={insertRef} onClick={mainAction}>
                        {buttonLabel}
                    </div>
                </div>
            </Breakpoint>
            <Breakpoint mobile only>
            <div className="ai-actions mobile">
                    <div className={mainActionClass} tabIndex="0" role="button" ref={insertRef} onClick={mainAction}>
                        {buttonLabel}
                    </div>
                    {startOver}
                </div>
            </Breakpoint>
        </div>
    );
};

export default AIHelper;
